import { AcrCategory, AcrProbability, AcrSeverity, type StudyAcrEntrySelectDto } from '@@types/api.types';
import { cn } from '@lib/utils';
import { useGetMeiaAcrRegister } from '@queries/meia.query';
import { ShieldAlertIcon } from 'lucide-react';
import { type FC, Fragment } from 'react';
import { getAcrProbabilityLabel, getAcrSeverityLabel } from './meia-acr.util';

type MeiaAcrMatrixProps = {
  onSelectEntry: (acrEntryId: string) => void;
  studyId: string;
};

// Rows: 5-band likelihood Very Low → Very High (top to bottom; the highest-risk cell sits bottom-right,
// matching the prototype's DoD 5×4 frame). Columns: 4-band impact Low → Critical (left to right).
const LIKELIHOOD_ROWS: AcrProbability[] = [
  AcrProbability.VERY_LOW,
  AcrProbability.LOW,
  AcrProbability.MEDIUM,
  AcrProbability.HIGH,
  AcrProbability.VERY_HIGH,
];
const IMPACT_COLS: AcrSeverity[] = [AcrSeverity.LOW, AcrSeverity.MEDIUM, AcrSeverity.HIGH, AcrSeverity.CRITICAL];

const LIKELIHOOD_WEIGHT: Record<AcrProbability, number> = {
  [AcrProbability.HIGH]: 4,
  [AcrProbability.LOW]: 2,
  [AcrProbability.MEDIUM]: 3,
  [AcrProbability.VERY_HIGH]: 5,
  [AcrProbability.VERY_LOW]: 1,
};

const IMPACT_WEIGHT: Record<AcrSeverity, number> = {
  [AcrSeverity.CRITICAL]: 4,
  [AcrSeverity.HIGH]: 3,
  [AcrSeverity.LOW]: 1,
  [AcrSeverity.MEDIUM]: 2,
};

// Composite-score cutoffs for the Low / Moderate / High legend bands. Score = likelihood weight (1–5) × impact
// weight (1–4), so the scale runs 1–20: ≥ HIGH_RISK_SCORE is red, ≥ MODERATE_RISK_SCORE is amber, else green.
const HIGH_RISK_SCORE = 10;
const MODERATE_RISK_SCORE = 5;

/** Composite severity → cell tint (High×Critical is red, Low×Low is green). */
const getCellTone = (likelihood: AcrProbability, impact: AcrSeverity): string => {
  const score = LIKELIHOOD_WEIGHT[likelihood] * IMPACT_WEIGHT[impact];
  if (score >= HIGH_RISK_SCORE) return 'bg-red-50 border-red-200';
  if (score >= MODERATE_RISK_SCORE) return 'bg-amber-50 border-amber-200';
  return 'bg-green-50 border-green-200';
};

export const MeiaAcrMatrix: FC<MeiaAcrMatrixProps> = (props) => {
  const { onSelectEntry, studyId } = props;
  const { data: entries = [], isLoading } = useGetMeiaAcrRegister({ studyId });

  const risks = entries.filter((entry) => entry.category === AcrCategory.RISK);
  const plottable = risks.filter((risk) => risk.likelihood && risk.impact);
  const unplottable = risks.filter((risk) => !risk.likelihood || !risk.impact);

  const getCellRisks = (likelihood: AcrProbability, impact: AcrSeverity): StudyAcrEntrySelectDto[] =>
    plottable.filter((risk) => risk.likelihood === likelihood && risk.impact === impact);

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-medium text-gray-800">Risk Matrix</h2>
        <p className="mt-1 text-xs text-gray-500">
          Likelihood × impact for this study's Risk entries (5-band likelihood, 4-band impact). Assumptions and
          constraints are not plotted.
        </p>
      </div>

      {risks.length === 0 ? (
        <p className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          No risks to plot yet.
        </p>
      ) : (
        <>
          {unplottable.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
              <ShieldAlertIcon className="mt-0.5 size-3.5 shrink-0" />
              <span>
                <span className="font-medium">BV-046:</span> {unplottable.length} risk
                {unplottable.length === 1 ? '' : 's'} cannot be plotted — missing likelihood and/or impact (
                {unplottable.map((risk) => risk.acrCode).join(', ')}). Open the register to fix.
              </span>
            </div>
          )}

          <div className="overflow-x-auto">
            <div className="inline-grid grid-cols-[auto_repeat(4,minmax(7rem,1fr))] gap-1">
              <div />
              {IMPACT_COLS.map((impact) => (
                <div
                  className="px-2 py-1 text-center text-[11px] font-medium text-gray-500"
                  key={impact}
                >
                  {getAcrSeverityLabel(impact)}
                </div>
              ))}

              {LIKELIHOOD_ROWS.map((likelihood) => (
                <Fragment key={likelihood}>
                  <div className="flex items-center justify-end px-2 py-1 text-right text-[11px] font-medium text-gray-500">
                    {getAcrProbabilityLabel(likelihood)}
                  </div>
                  {IMPACT_COLS.map((impact) => {
                    const cellRisks = getCellRisks(likelihood, impact);
                    return (
                      <div
                        className={cn('min-h-20 rounded-md border p-1.5', getCellTone(likelihood, impact))}
                        key={impact}
                      >
                        <div className="mb-1 text-[10px] font-medium text-gray-500">
                          {cellRisks.length} risk{cellRisks.length === 1 ? '' : 's'}
                        </div>
                        <div className="flex flex-col gap-1">
                          {cellRisks.map((risk) => (
                            <button
                              className="block w-full rounded border border-gray-200 bg-white px-1.5 py-1 text-left transition-colors hover:border-purple-300 hover:bg-purple-50"
                              key={risk.id}
                              onClick={() => onSelectEntry(risk.id)}
                              title={risk.statement}
                              type="button"
                            >
                              <span className="block font-mono text-[10px] font-semibold text-gray-700">
                                {risk.acrCode}
                              </span>
                              {risk.title && (
                                <span className="block truncate text-[10px] text-gray-600">{risk.title}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-[11px] text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="size-3 rounded-sm border border-green-200 bg-green-50" />
              Low
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-3 rounded-sm border border-amber-200 bg-amber-50" />
              Moderate
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-3 rounded-sm border border-red-200 bg-red-50" />
              High
            </span>
            <span className="text-gray-400">
              Tinted by composite likelihood × impact. Select an entry to open it in the register.
            </span>
          </div>
        </>
      )}
    </div>
  );
};
